import {
  type IconName,
  SelectField,
  type SelectFieldOption,
} from '@wandb/components';
import {
  Content,
  Portal,
  Provider,
  Root,
  Trigger,
} from '@wandb/weave/components/RadixTooltip';
import React, {type FC, useState} from 'react';
import {createPortal} from 'react-dom';

import {
  AccessOption,
  getDescriptionForProjectAccess,
  getDisplayNameForProjectAccess,
  getIconForProjectAccess,
} from '../../../util/permissions';

interface ProjectAccessOptionsProps {
  selectedProjectAccess: AccessOption | undefined;
  setSelectedProjectAccess: (accessOption: AccessOption) => void;
  /** Options that are shown but cannot be selected, with the reason why. */
  disabledAccessReasons: Partial<Record<AccessOption, string>>;
  isTeamProject: boolean;
  isRestrictedProjectEnabled: boolean;
}

const PROJECT_ACCESS_OPTIONS = [
  AccessOption.Private,
  AccessOption.Public,
  AccessOption.Open,
  AccessOption.Restricted,
];

type HoveredOption = {
  reason: string;
  rect: DOMRect;
};

/**
 * SelectField options cannot carry their own tooltip, so this finds the
 * disabled option row under the pointer from the `data-key` react-aria puts on
 * each row.
 */
const getHoveredDisabledOption = (
  target: EventTarget,
  disabledAccessReasons: Partial<Record<AccessOption, string>>
): HoveredOption | null => {
  const option =
    target instanceof Element
      ? target.closest('[role="option"][data-key]')
      : null;
  const key = option?.getAttribute('data-key') as AccessOption | undefined;
  const reason = key != null ? disabledAccessReasons[key] : undefined;
  return option != null && reason != null
    ? {reason, rect: option.getBoundingClientRect()}
    : null;
};

const DisabledOptionTooltip: FC<HoveredOption> = ({reason, rect}) =>
  createPortal(
    <Provider>
      <Root open>
        <Trigger asChild>
          <div
            aria-hidden
            style={{
              position: 'fixed',
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
              pointerEvents: 'none',
            }}
          />
        </Trigger>
        <Portal>
          <Content side="top" align="center">
            {reason}
          </Content>
        </Portal>
      </Root>
    </Provider>,
    document.body
  );

export const ProjectAccessOptions: FC<ProjectAccessOptionsProps> = ({
  selectedProjectAccess,
  setSelectedProjectAccess,
  disabledAccessReasons,
  isTeamProject,
  isRestrictedProjectEnabled,
}) => {
  const [hoveredDisabledOption, setHoveredDisabledOption] =
    useState<HoveredOption | null>(null);
  const clearHoveredDisabledOption = () => setHoveredDisabledOption(null);

  const isPremiumFeatureEnabled = isRestrictedProjectEnabled && isTeamProject;

  const items: SelectFieldOption[] = PROJECT_ACCESS_OPTIONS.map(o => ({
    id: o,
    label: getDisplayNameForProjectAccess(o, isTeamProject),
    description: getDescriptionForProjectAccess(o, isTeamProject),
    decorationLeft: {
      iconProps: {
        icon: getIconForProjectAccess(o, isTeamProject) as IconName,
      },
    },
    ...(o === AccessOption.Restricted && !isPremiumFeatureEnabled
      ? {
          decorationRight: {
            pillProps: {
              icon: 'crown-pro' as const,
              color: 'purple' as const,
              tooltip: {children: 'Upgrade to enable this feature'},
            },
          },
        }
      : {}),
  }));

  const disabledKeys = PROJECT_ACCESS_OPTIONS.filter(
    o => disabledAccessReasons[o] != null
  );
  if (!isPremiumFeatureEnabled) {
    disabledKeys.push(AccessOption.Restricted);
  }

  return (
    // The listbox is portaled, but React pointer events still bubble here.
    // Moving off the rows lands on react-aria's underlay, also in the portal.
    <div
      className="contents"
      onPointerOver={event =>
        setHoveredDisabledOption(
          getHoveredDisabledOption(event.target, disabledAccessReasons)
        )
      }
      onPointerLeave={clearHoveredDisabledOption}>
      <SelectField
        id="project-access-options"
        label="Project visibility"
        items={items}
        placeholder="Select project visibility"
        value={selectedProjectAccess ?? null}
        onChange={key => {
          if (key != null) {
            setSelectedProjectAccess(key as AccessOption);
          }
        }}
        onOpenChange={isOpen => {
          if (!isOpen) {
            clearHoveredDisabledOption();
          }
        }}
        disabledKeys={disabledKeys}
        fullWidth
        matchTriggerWidth
      />
      {hoveredDisabledOption != null && (
        <DisabledOptionTooltip {...hoveredDisabledOption} />
      )}
    </div>
  );
};
